import { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { Button, Colors, fontMedium, Icon } from "swing-components";
import { phoneNumberFormatter } from "swing-utils";

import { ErrorMessage, Toast, VerificationCodeInput } from "~components";
import type { PhoneVerifyAnswerType } from "~onboarding/utils";
import {
  PhoneVerificationError,
  PhoneVerificationPayload,
  PhoneVerificationSuccess,
  POST,
  translate,
  useLocalStorage,
} from "~utils";

const PHONE_NUMBER_STORAGE_KEY = "onboarding-phone-number";

type LocalErrorType = "VERIFY_CODE_ERROR" | "REQUEST_CODE_ERROR" | null;

type PhoneVerifyAnswerProps = Omit<PhoneVerifyAnswerType, "type"> & {
  /**
   * The value stored in the onboarding attribute.
   */
  value?: string;
  /**
   * When entering a verification code, pass the `value` to the parent.
   * In this case, we simply pass "valid" to indicate that the answer
   * is complete.
   */
  onChange: (value?: string) => void;
  /**
   * Disables the editability of the answer.
   */
  isReadOnly?: boolean;
};

export function PhoneVerifyAnswer(props: PhoneVerifyAnswerProps) {
  const { onChange, isReadOnly } = props;
  const [verificationCode, setVerificationCode] = useState<string>("");
  const [localError, setLocalError] = useState<LocalErrorType>(null);
  const [phoneNumber, setPhoneNumber] = useLocalStorage(PHONE_NUMBER_STORAGE_KEY, "");
  const [showToast, setShowToast] = useState(false);
  const { mutate: requestCode } = useMutation<
    PhoneVerificationSuccess | PhoneVerificationError,
    AxiosError,
    PhoneVerificationPayload
  >({
    mutationFn: (payload) => POST("/api/sub/phone-verification", payload),
    onSuccess: () => setShowToast(true),
    onError: () => setLocalError("REQUEST_CODE_ERROR"),
  });
  const {
    mutate: verifyCode,
    isPending: isVerifyCodePending,
    isSuccess: isVerifyCodeSuccess,
  } = useMutation<
    PhoneVerificationSuccess | PhoneVerificationError,
    AxiosError,
    PhoneVerificationPayload
  >({
    mutationFn: (payload) => POST("/api/sub/phone-verification", payload),
    onSuccess: () => {
      onChange("valid");
      setPhoneNumber(phoneNumber);
    },
    onError: () => setLocalError("VERIFY_CODE_ERROR"),
  });
  const isPhoneNumberValid = phoneNumber.length === 10;
  const isVerificationCodeValid = verificationCode.length === 4;
  const isVerifyCodeButtonDisabled =
    isReadOnly ||
    localError ||
    !isVerificationCodeValid ||
    isVerifyCodePending ||
    isVerifyCodeSuccess;

  function handleVerifyCode() {
    if (showToast) {
      setShowToast(false);
    }
    if (isVerificationCodeValid && !isVerifyCodeSuccess) {
      verifyCode({ phoneNumber, verificationCode });
    }
  }

  function handleRequestCode() {
    setLocalError(null);
    requestCode({ phoneNumber });
  }

  useEffect(() => {
    if (!isVerificationCodeValid) {
      setLocalError(null);
    }
  }, [verificationCode]);

  return (
    <div css={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {!isPhoneNumberValid ? (
        <ErrorMessage>
          <span>{translate("PHONE_VERIFICATION_MISSING_PHONE_NUMBER_ERROR")}</span>
        </ErrorMessage>
      ) : (
        <span>
          {`${translate("PHONE_VERIFICATION_ENTER_CODE_ALERT_MSG")} ${phoneNumberFormatter({ phoneNumber }).value}`}
        </span>
      )}
      <VerificationCodeInput
        value={verificationCode}
        onChange={(v) => setVerificationCode(v)}
        isDisabled={isReadOnly || !isPhoneNumberValid}
      />
      {localError === "VERIFY_CODE_ERROR" && (
        <ErrorMessage>{translate("PHONE_VERIFICATION_ENTER_CODE_ALERT_ERROR")}</ErrorMessage>
      )}
      {localError === "REQUEST_CODE_ERROR" && (
        <ErrorMessage>{translate("PHONE_VERIFICATION_ENTER_CODE_GENERAL_ERROR")}</ErrorMessage>
      )}
      {isVerifyCodeSuccess && (
        <div css={{ display: "flex", gap: 8, alignItems: "center" }}>
          <Icon name="Check Circle" color={Colors.green500} />
          <span css={fontMedium({ fontWeight: 600, color: "green500" })}>
            {translate("PROFILE_PHONE_NUMBER_SUCCESS_CONTENT_TITLE")}
          </span>
        </div>
      )}
      <Button
        type="primary"
        isFluid
        status={
          isVerifyCodeButtonDisabled ? "disabled" : isVerifyCodePending ? "loading" : "enabled"
        }
        onClick={handleVerifyCode}
      >
        {translate("LABEL_VERIFY")}
      </Button>
      <div css={{ fontSize: "14px" }}>
        <span>{translate("PHONE_VERIFICATION_ENTER_CODE_MISSING_CODE")}</span>{" "}
        <span
          onClick={handleRequestCode}
          css={{ color: Colors.blue500, textDecoration: "underline", cursor: "pointer" }}
        >
          {translate("PHONE_VERIFICATION_ENTER_CODE_REQUEST_NEW_CODE")}
        </span>
      </div>
      <Toast
        isVisible={showToast}
        type="positive"
        onDismiss={() => setShowToast(false)}
        text={`${translate("PHONE_VERIFICATION_ENTER_CODE_TOAST_MSG")} to ${phoneNumberFormatter({ phoneNumber }).value}`}
      />
    </div>
  );
}
